import './CreateEditDialog.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog } from 'primereact/dialog';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ToastContext from '../../../../../context/ToastContext';
import useAxios from '../../../../../hooks/useAxios';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import { CreateOrderResource } from '../../../../../types/api/orders';
import { noop } from '../../../../../utils/helpers/functions';
import { getChangedValues } from '../../../../../utils/helpers/object';
import {
  errorToast,
  infoToast,
  successToast,
} from '../../../../../utils/helpers/primereact';
import DialogSpinner from '../../../../Dialogs/DialogSpinner/DialogSpinner';
import { SingleOrder } from '../../Orders.functions';
import {
  FormValues,
  getInitialValues,
  getValidationSchema,
  toApiData,
} from './CreateEditDialog.functions';
import { DialogFooter } from './DialogFooter';
import { DialogHeader } from './DialogHeader';
import FormContainer from './FormContainer';

type Props = {
  visible: boolean;
  data?: SingleOrder | any;
  isEditDialog?: boolean;
  isWarehouseReception?: boolean;
  isLoading?: boolean;
  onHide: () => void;
  reload: () => void;
  isSmallScreen: boolean;
  isGroupDialog?: boolean;
  orderData?: FormValues[];
  setOrderData?: Dispatch<SetStateAction<FormValues>>;
  setOrderIndex?: Dispatch<SetStateAction<number>>;
  orderIndex?: number;
  title?: string;
  isConfigVisible?: boolean;
  constantFields?: any;
  initialCreationValues?: Partial<FormValues>;
  isSenderTabDisabled?: boolean;
  onCreate?: (response: CreateOrderResource) => void;
  onEdit?: (id: number) => void;
};

function CreateEditDialog({
  visible,
  data,
  isEditDialog = false,
  isWarehouseReception = false,
  isLoading = false,
  onHide,
  reload,
  isSmallScreen,
  isGroupDialog = false,
  orderData,
  setOrderData = noop,
  setOrderIndex,
  orderIndex = 0,
  title,
  isConfigVisible,
  constantFields = {},
  initialCreationValues,
  isSenderTabDisabled,
  onCreate,
  onEdit,
}: Props) {
  const { t } = useTranslation();
  const toastRef = useContext(ToastContext)?.toastRef!;
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const defaultValues = useMemo(
    () =>
      getInitialValues({
        data,
        isEditDialog,
        isWarehouseReception,
        isGroupDialog,
        groupOrderData: orderData?.[orderIndex] ?? ([] as any),
        initialCreationValues: initialCreationValues ?? {},
      }),
    [
      data,
      initialCreationValues,
      isEditDialog,
      isGroupDialog,
      isWarehouseReception,
      orderData,
      orderIndex,
    ]
  );

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    control,
    formState: { errors },
  } = methods;
  // console.log('values ', getValues());
  console.log('errors ', errors);

  const { reload: formSubmitRequest, isLoading: isFormSubmitRequestLoading } =
    useAxios();

  const { reload: pickupAssignmentRequest } = useAxios();

  function handleFormSubmission(values: FormValues) {
    if (isEditDialog) {
      const initialValues = toApiData(control._defaultValues as any, true);
      const currentValues = toApiData(getValues(), true);
      const apiData = getChangedValues(currentValues, initialValues);

      // If there are no changes introduced, don't contaminate the API
      if (!Object.keys(apiData).length) {
        infoToast(
          toastRef,
          t('No changes made'),
          t("You haven't made any changes yet.")
        );

        return;
      }

      formSubmitRequest({
        url: `/orders/${data?.id}`,
        payload: apiData,
        method: 'PUT',
        successCallback: (data: any) => {
          console.log('update order data responee ', data);
          successToast(
            toastRef,
            t('Order Updated'),
            t('Order updated sucessfully.')
          );

          reset();
          onEdit?.(data?.id);
          reload();
          onHide();
        },
        errorCallback: (err: any) => {
          console.log('err ', err);
          errorToast(
            toastRef,
            t('Order Update Failed'),
            t(
              'An error occured while trying to edit Order. Please check your inputs.'
            )
          );
        },
      });
    } else {
      formSubmitRequest({
        url: '/orders/add',
        payload: toApiData(values),
        method: 'POST',
        successCallback: (data: any) => {
          console.log('order data response ', data);
          if (
            data &&
            getValues()?.pickupAssignmentCheckbox &&
            getValues()?.pickupAssignmentCourier
          ) {
            pickupAssignmentRequest({
              url: '/orders/courier/assignments/pickup',
              method: 'PUT',
              data: {
                courier_id: getValues()?.pickupAssignmentCourier,
                parcels: [data.id],
              },
              successCallback: () => {
                successToast(
                  toastRef,
                  t('Order #{{orderID}} Assigned', {
                    orderID: data?.id,
                  }),
                  t(
                    'The order has been successfully assigned to the requested courier'
                  )
                );
              },
            });
          }

          reset();
          onCreate?.(data);
          reload();
          onHide();
        },
        errorCallback: (err: any) => {
          console.log('err ', err);
          errorToast(
            toastRef,
            t('Order Creation Failed'),
            t(
              'An error occured while trying to create Order. Please check your inputs.'
            )
          );
        },
      });
    }
  }

  useEffect(() => {
    reset();
  }, [visible, reset]);

  // useEffect(() => {
  //   if (!isGroupDialog) {
  //     return;
  //   }

  // setValue(orderData?.[orderIndex] ?? getValues());
  // }, [orderIndex, orderData, isGroupDialog]);

  useEffect(() => {
    if (!isGroupDialog) return;

    const newValues = orderData?.[orderIndex] ?? getValues();

    reset(newValues);
  }, [orderIndex, orderData, isGroupDialog, reset, getValues]);

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  return (
    <FormProvider {...methods}>
      <form
        id="order-create-edit-form"
        onSubmit={handleSubmit(handleFormSubmission)}
      >
        <Dialog
          visible={visible}
          className={`create-edit-parcel-dialog ${isConfigVisible && 'small'}`}
          header={
            <DialogHeader
              title={title}
              isEditDialog={isEditDialog}
              isGroupDialog={isGroupDialog}
              serialNumber={
                isGroupDialog
                  ? orderData?.[orderIndex]?.seriski_broj
                  : data?.seriski_broj
              }
              orderIndex={orderIndex}
              ordersCount={orderData?.length ?? 0}
            />
          }
          footer={
            <DialogFooter
              isEditDialog={isEditDialog}
              isGroupDialog={isGroupDialog}
              isFormSubmitRequestLoading={isFormSubmitRequestLoading}
              onHide={onHide}
              setOrderIndex={setOrderIndex}
              orderIndex={orderIndex}
              orderData={orderData}
              setOrderData={setOrderData}
              isTabs={isSmallScreen}
              onCreate={onCreate}
            />
          }
          onHide={onHide}
          blockScroll
          maximizable
          maximized={isOnMobile}
        >
          {isLoading ? (
            <DialogSpinner />
          ) : (
            <FormContainer
              constantFields={constantFields}
              isEditDialog={isEditDialog}
              isTabs={isSmallScreen}
              isWarehouseReception={isWarehouseReception}
              orderIndex={orderIndex}
              isSenderTabDisabled={isSenderTabDisabled}
            />
          )}
        </Dialog>
      </form>
    </FormProvider>
  );
}

export default CreateEditDialog;
